/**
 * How the builder reflows.
 *
 * both — elements reduce vertically and horizontally.
 * vertical — vertically only.
 * compact — as `both`, plus a height trigger: a wide but short window also gets
 *   the reduced settings.
 */
export type ResponsiveType = 'both' | 'vertical' | 'compact';
/** Not offered in the options panel for now — nothing reads it yet. */
export type SettingsVariant = 'default' | 'dividers';

/**
 * How the top bar offers the chat, and what the row does when it closes. The
 * options panel numbers these by position, so its order is the one to trust —
 * these names stay put when that order changes.
 *
 * closeCross — the Chat button gives way to a cross at the chat's far edge, and
 *   closing the chat hands the row back to the settings bar.
 * toggleButton — one button that reads Chat, then Hide while the chat is open.
 * selectionOnly — the cross as above, but closing the chat gives the whole row
 *   to the email. The settings bar only appears once something is selected.
 * menuAndClose — closeCross with a hamburger for the sidebar, and the cross kept
 *   beside it rather than out at the chat's far edge.
 */
export type NavigationVariant = 'closeCross' | 'toggleButton' | 'selectionOnly' | 'menuAndClose';

/**
 * The two behaviours this project explores. Both are numbered by position in the
 * options panel, so its order is the one to trust — the delete section leads with
 * `option2`, the minus astride the corner, which it therefore labels Option 1.
 */
export type DeleteElementVariant = 'option1' | 'option2' | 'option3' | 'option4' | 'option5';

/**
 * The font mapping section leads with `option1`, which is the email left as it is —
 * the panel labels it "No mapping applied" rather than numbering it, so its Option 1
 * is `option2`, the first of the ways the roles are offered in the text toolbar.
 */
export type FontMappingVariant =
  | 'option1'
  | 'option2'
  | 'option3'
  | 'option4'
  | 'option5'
  | 'option6';

/**
 * How the colour picker itself is offered. Numbered by position in the options
 * panel, as the sections above are.
 *
 * option1 — pages of suggestions scrolled through with arrows, worked out from the
 *   brand first and from the selection last.
 * option2 — the same three switched between on a bar, led by the colour in hand:
 *   its shades, then what sits near it, then the brand's own.
 */
export type ColorPickerVariant = 'option1' | 'option2';

export type PrototypeOptions = {
  responsiveType: ResponsiveType;
  settingsVariant: SettingsVariant;
  navigationVariant: NavigationVariant;
  deleteElementVariant: DeleteElementVariant;
  fontMappingVariant: FontMappingVariant;
  colorPickerVariant: ColorPickerVariant;
};

export const defaultPrototypeOptions: PrototypeOptions = {
  responsiveType: 'both',
  settingsVariant: 'default',
  navigationVariant: 'closeCross',
  /* The delete section's first row, which is the minus control. */
  deleteElementVariant: 'option2',
  fontMappingVariant: 'option1',
  colorPickerVariant: 'option1',
};
